import { t } from '@lingui/core/macro';
import { AbcClassificationLabel } from '@luminovo/api-contracts';
import { assertUnreachable } from '@luminovo/commons';
import { Tag, Tooltip } from '@luminovo/design-system';
import { formatDecimal, transEnum } from '@luminovo/frontend-utils';
import type { JSX } from 'react';

import { abcClassificationLabelTranslations } from '../../i18n';

export interface AbcClassificationTagProps {
    label: AbcClassificationLabel;
    reason?: {
        type: 'CostPercentage' | 'NoOffer' | 'Consigned';
        data?: number;
    };
    showTooltip?: boolean;
    attention?: 'low' | 'high';
}

function getAbcClassificationColor(label: AbcClassificationLabel): 'green' | 'yellow' | 'neutral' | 'violet' {
    switch (label) {
        case AbcClassificationLabel.A:
            return 'green'; // High value - important, priority
        case AbcClassificationLabel.B:
            return 'yellow'; // Medium value - attention needed
        case AbcClassificationLabel.C:
            return 'neutral'; // Low value - routine handling
        case AbcClassificationLabel.Unknown:
            return 'violet'; // Unknown - no offers or consigned parts
        default:
            assertUnreachable(label);
    }
}

function formatAbcClassificationReason(
    classification: AbcClassificationLabel,
    reason?: AbcClassificationTagProps['reason'],
): string {
    const label = transEnum(classification, abcClassificationLabelTranslations);

    if (!reason) {
        return label;
    }

    switch (reason.type) {
        case 'CostPercentage':
            const percentage = reason.data ?? 0;
            if (percentage >= 20) {
                return t`${label}: High individual cost impact (${formatDecimal(percentage)}% of total)`;
            } else if (percentage >= 5) {
                return t`${label}: Medium individual cost impact (${formatDecimal(percentage)}% of total)`;
            } else {
                return t`${label}: Low individual cost impact (${formatDecimal(percentage)}% of total)`;
            }
        case 'NoOffer':
            return t`${label}: No offers available`;
        case 'Consigned':
            return t`${label}: Consigned part`;
        default:
            assertUnreachable(reason.type);
    }
}

export function AbcClassificationTag({
    label,
    reason,
    showTooltip = false,
    attention = 'low',
}: AbcClassificationTagProps): JSX.Element {
    const color = getAbcClassificationColor(label);
    const displayLabel = transEnum(label, abcClassificationLabelTranslations);

    const tag = <Tag color={color} attention={attention} label={displayLabel} />;

    if (showTooltip) {
        const tooltipText = formatAbcClassificationReason(label, reason);
        return (
            <Tooltip variant="white" disableMaxWidth={true} title={tooltipText}>
                {tag}
            </Tooltip>
        );
    }

    return tag;
}
